import http from '../../../services/api'
import type { APIResponse, Filters, APIPaginatedResponse, Pagination } from '../../../services/types'
import { getRequestParamString } from '../../../services/utils/utils'
import { ITEM_GROUP } from '../../../constants/ItemGroup'
import type {
  InventoryCreateItemApi,
  InventoryHealthSummaryApi,
  InventoryItemApi,
  ItemAvailableStockApi,
} from '../types'

const apiPath = import.meta.env.VITE_API_PATH

const PRODUCT_ITEM_GROUP_ID = ITEM_GROUP.find((g) => g.name === 'Product')?.value

function itemGroupParamString(paramString: string): string {
  return PRODUCT_ITEM_GROUP_ID != null ? `${paramString ? '&' : '?'}item_group_id=${PRODUCT_ITEM_GROUP_ID}` : ''
}

async function getItems(filters?: Partial<Filters>, pagination?: Partial<Pagination>) {
  const paramString = getRequestParamString(filters, pagination)
  return await http.get<APIPaginatedResponse<any>>(apiPath + 'items' + paramString + itemGroupParamString(paramString))
}

export type GetItemsWithTypeNamesOptions = {
  /** When true (default), only inventory (product) items are returned. Set false for sales etc. to include services. */
  productGroupOnly?: boolean
}

async function getItemsWithTypeNames(
  filters?: Partial<Filters>,
  pagination?: Partial<Pagination>,
  options?: GetItemsWithTypeNamesOptions,
) {
  const paramString = getRequestParamString(filters, pagination)
  const productGroupOnly = options?.productGroupOnly !== false
  const groupSuffix = productGroupOnly ? itemGroupParamString(paramString) : ''
  return await http.get<APIPaginatedResponse<any>>(apiPath + 'items/with-type-name' + paramString + groupSuffix)
}

async function getItemAvailableStock(itemUuid: string) {
  return await http.get<APIResponse<ItemAvailableStockApi>>(`${apiPath}items/${itemUuid}/available-stock`)
}

async function getAvailableStockByLocation(itemUuid: string, options?: { excludeOtherLocations?: boolean }) {
  const params = options?.excludeOtherLocations ? '?exclude_other_locations=true' : ''
  return await http.get<any>(`${apiPath}items/${itemUuid}/available-stock/locations${params}`)
}

/** Stock for one item at one inventory location (backend aggregates qty for that location). */
async function getAvailableStockAtLocation(itemUuid: string, locationId: number, unitUuid?: string | null) {
  const params = unitUuid ? `?unit_uuid=${encodeURIComponent(unitUuid)}` : ''
  return await http.get<any>(`${apiPath}items/${itemUuid}/available-stock/locations/${locationId}${params}`)
}

async function getAvailableStockAtLocationBatch(
  itemUuid: string,
  locationId: number,
  batchNumber: string,
  unitUuid?: string | null,
) {
  const safeBatch = encodeURIComponent(batchNumber)
  const params = unitUuid ? `?unit_uuid=${encodeURIComponent(unitUuid)}` : ''
  return await http.get<any>(
    `${apiPath}items/${itemUuid}/available-stock/locations/${locationId}/batch/${safeBatch}${params}`,
  )
}

async function deleteItem(ItemUuid: string) {
  return await http.delete<APIResponse<boolean>>(`${apiPath}items/${ItemUuid}`)
}

async function createItem(input: InventoryCreateItemApi) {
  const payLoad = {
    name: input.name,
    code: input.code,
    unit_uuid: input.unit_id || input.unit_uuid,
    item_group_id: PRODUCT_ITEM_GROUP_ID,
    item_category_uuid: input.item_category_uuid,
    item_type_uuid: input.item_type_uuid,
    expires: input.expires,
    loyalty_applicable: input.loyalty_applicable,
    bar_code: input.bar_code,
    price: input.price,
    notification_level: input.notification_level,
    image_url: input.image_url,
    serial_number: input.serial_number,
    size: input.size,
    color: input.color,
    description: input.description,
    preparation_time: input.preparation_time,
    nutrition_info: input.nutrition_info,
    special_tags: input.special_tags,
    popularity_rating: input.popularity_rating,
    ingredients: input.ingredients,
    batch_tracking: input.batch_tracking ?? false,
    serial_tracking: input.serial_tracking ?? false,
    allow_negative_stock: input.allow_negative_stock ?? false,
    reorder_quantity: input.reorder_quantity ?? null,
    default_cost: input.default_cost ?? null,
  }
  return await http.post<APIResponse<any>>(apiPath + 'items', payLoad)
}

async function updateItem(input: InventoryItemApi) {
  const payLoad = {
    name: input.name,
    code: input.code,
    unit_uuid: input.unit_id || input.unit_uuid,
    item_group_id: PRODUCT_ITEM_GROUP_ID,
    item_category_uuid: input.item_category_uuid,
    item_type_uuid: input.item_type_uuid,
    expires: input.expires,
    loyalty_applicable: input.loyalty_applicable,
    bar_code: input.bar_code,
    price: input.price,
    notification_level: input.notification_level,
    image_url: input.image_url,
    serial_number: input.serial_number,
    size: input.size,
    color: input.color,
    description: input.description,
    preparation_time: input.preparation_time,
    nutrition_info: input.nutrition_info,
    special_tags: input.special_tags,
    popularity_rating: input.popularity_rating,
    ingredients: input.ingredients,
    batch_tracking: input.batch_tracking ?? false,
    serial_tracking: input.serial_tracking ?? false,
    allow_negative_stock: input.allow_negative_stock ?? false,
    reorder_quantity: input.reorder_quantity ?? null,
    default_cost: input.default_cost ?? null,
  }
  return await http.patch<APIResponse<boolean>>(`${apiPath}items/${input.uuid}`, payLoad)
}

async function getItemsOfItemType(itemTypeUuid: string) {
  return await http.get<APIResponse<InventoryItemApi[]>>(`${apiPath}items/item-type/${itemTypeUuid}`)
}

/** Inventory health counts for the active business and warehouse. */
async function getInventoryStatusSummary() {
  return await http.get<APIResponse<InventoryHealthSummaryApi>>(apiPath + 'items/inventory-status-summary', {
    errorHandle: false,
  } as any)
}

/** Fetch one item by uuid (list filter) for form refresh after unit changes. */
async function getItemByUuid(itemUuid: string) {
  const paramString = getRequestParamString({ column: 'uuid', search: itemUuid }, { page: 1, perPage: 1 })
  const groupSuffix = itemGroupParamString(paramString)
  return await http.get<APIPaginatedResponse<InventoryItemApi>>(
    apiPath + 'items/with-type-name' + paramString + groupSuffix,
  )
}

const itemsService = {
  getItems,
  deleteItem,
  createItem,
  updateItem,
  getItemsOfItemType,
  getItemsWithTypeNames,
  getItemAvailableStock,
  getAvailableStockByLocation,
  getAvailableStockAtLocation,
  getAvailableStockAtLocationBatch,
  getItemByUuid,
  getInventoryStatusSummary,
}

export default itemsService
